import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtMoney, fmtPrice, fmtPct, pctClass } from "@/lib/format";

export default function Portfolio() {
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/portfolio/positions"),
      api.get("/portfolio/summary"),
    ]);
    setPositions(p.data);
    setSummary(s.data);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  return (
    <Layout>
      <div className="p-6">
        <header className="mb-6">
          <h1 className="text-2xl font-medium tracking-tight">Portfolio</h1>
          <p className="text-xs text-[#8B95A5] mt-1">Unified positions across every connected broker.</p>
        </header>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#272A30] mb-6" data-testid="portfolio-stats">
            <Stat label="Market Value" value={fmtMoney(summary.total_market_value)} />
            <Stat label="Unrealized P&L" value={fmtMoney(summary.total_unrealized_pnl)} cls={pctClass(summary.total_unrealized_pnl)} />
            <Stat label="Return %" value={fmtPct(summary.total_unrealized_pnl_pct)} cls={pctClass(summary.total_unrealized_pnl_pct)} />
            <Stat label="Cash Available" value={fmtMoney(summary.cash_total)} />
          </div>
        )}

        <div className="panel" data-testid="positions-table">
          <div className="panel-header"><span>Open Positions</span><span className="text-[10px] font-mono text-[#4B5563]">{positions.length} positions</span></div>
          <table className="w-full text-xs">
            <thead className="border-b border-[#272A30] text-[10px] uppercase tracking-[0.2em] text-[#8B95A5]">
              <tr>
                <th className="text-left p-3">Symbol</th>
                <th className="text-left p-3">Broker</th>
                <th className="text-right p-3">Qty</th>
                <th className="text-right p-3">Avg Price</th>
                <th className="text-right p-3">Last</th>
                <th className="text-right p-3">Market Value</th>
                <th className="text-right p-3">P&L</th>
                <th className="text-right p-3">% Chg</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-[#4B5563] text-xs">No positions yet. Approve a buy signal to begin.</td></tr>
              ) : positions.map((p) => (
                <tr key={p.id} className="border-b border-[#272A30] hover:bg-[#1A1D24]" data-testid={`pos-row-${p.symbol}`}>
                  <td className="p-3"><span className="font-medium">{p.symbol}</span> <span className="text-[10px] text-[#4B5563] uppercase">{p.asset_class}</span></td>
                  <td className="p-3 text-[#8B95A5] capitalize">{p.broker_id}</td>
                  <td className="p-3 text-right font-mono">{fmtPrice(p.quantity, 4)}</td>
                  <td className="p-3 text-right font-mono">{fmtPrice(p.avg_price)}</td>
                  <td className="p-3 text-right font-mono">{fmtPrice(p.current_price)}</td>
                  <td className="p-3 text-right font-mono">{fmtMoney(p.market_value)}</td>
                  <td className={`p-3 text-right font-mono ${pctClass(p.unrealized_pnl)}`}>{fmtMoney(p.unrealized_pnl)}</td>
                  <td className={`p-3 text-right font-mono ${pctClass(p.unrealized_pnl_pct)}`}>{fmtPct(p.unrealized_pnl_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value, cls = "" }) {
  return (
    <div className="bg-[#11141A] p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B95A5]">{label}</div>
      <div className={`mt-2 text-xl font-mono ${cls}`}>{value}</div>
    </div>
  );
}
